/**
 * One override row: multiple countries sharing the same threshold — a pinned
 * number, or (via the ⇄ toggle, mirroring the condition row) their own
 * COMPARISON against another metric over its own window.
 */

import type { FC } from 'react';
import { useCallback } from 'react';
import { ArrowLeftRight, Hash, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { OverrideCompareControls } from '@/features/tag-rule-compare/controls/OverrideCompareControls';
import type { Country } from '@/hooks/smswiser/core/useSMSWiser';
import { cn } from '@/lib/utils';

import { useSyncedThresholdInput } from '../use-synced-threshold-input';
import { CountryMultiPicker } from './CountryMultiPicker';
import type { CountryThresholdUIGroup } from './country-threshold-parts';

interface CountryOverrideRowProps {
  group: CountryThresholdUIGroup;
  options: Country[];
  maxKeys: number;
  isLoading: boolean;
  onPatch: (
    id: string,
    patch: Partial<
      Pick<CountryThresholdUIGroup, 'countryKeys' | 'value' | 'compareTo'>
    >,
  ) => void;
  onRemove: (id: string) => void;
  fields?: ReadonlyArray<{ key: string; label: string }>;
}

export const CountryOverrideRow: FC<CountryOverrideRowProps> = ({
  group,
  options,
  maxKeys,
  isLoading,
  onPatch,
  onRemove,
  fields,
}) => {
  const commitValue = useCallback((n: number) => onPatch(group.id, { value: n }), [group.id, onPatch]);
  const threshold = useSyncedThresholdInput(group.value, commitValue);
  const compare = group.compareTo;

  const picker = (
    <CountryMultiPicker
      options={options}
      selectedKeys={group.countryKeys}
      maxKeys={maxKeys}
      disabled={isLoading}
      onChangeKeys={(keys) => {
        const unique = [...new Set(keys.map((k) => k.trim().toLowerCase()))];
        onPatch(group.id, { countryKeys: unique });
      }}
    />
  );
  const removeBtn = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-9 w-9 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      onClick={() => onRemove(group.id)}
      aria-label="Remove country override"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );

  if (compare) {
    return (
      <div className="space-y-2 border-b border-border/40 px-4 py-3 last:border-b-0 hover:bg-background/40">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">{picker}</div>
          <button
            type="button"
            onClick={() => onPatch(group.id, { compareTo: undefined })}
            title="Pin a fixed value for these countries instead"
            aria-label="Use a fixed value for this override"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <Hash className="h-4 w-4" />
          </button>
          {removeBtn}
        </div>
        <OverrideCompareControls
          ct={compare}
          onChange={(ct) => onPatch(group.id, { compareTo: ct })}
          fields={fields}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-2 border-b border-border/40 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_5.5rem_2.5rem_2.5rem] sm:items-center sm:gap-3 sm:py-2',
        'last:border-b-0 hover:bg-background/40',
      )}
    >
      <div className="min-w-0">
        <span className="mb-1 block text-[11px] font-medium text-muted-foreground sm:sr-only">
          Countries
        </span>
        {picker}
      </div>
      <div className="min-w-0 sm:text-right">
        <span className="mb-1 block text-[11px] font-medium text-muted-foreground sm:sr-only">
          Value
        </span>
        <Input
          type="number"
          value={threshold.display}
          onChange={(e) => threshold.onChange(e.target.value)}
          onBlur={threshold.onBlur}
          onKeyDown={threshold.onKeyDown}
          className="h-9 w-full font-medium tabular-nums sm:max-w-[5.5rem] sm:ml-auto"
          aria-label="Value for selected countries"
        />
      </div>
      <div className="flex justify-end sm:justify-center">
        <button
          type="button"
          onClick={() =>
            onPatch(group.id, {
              compareTo: { field: fields?.[0]?.key ?? 'profit' },
            })
          }
          title="Compare these countries against another metric instead of a fixed value"
          aria-label="Compare this override against a metric"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
        >
          <ArrowLeftRight className="h-4 w-4" />
        </button>
      </div>
      <div className="flex justify-end sm:justify-center sm:pt-0">{removeBtn}</div>
    </div>
  );
};
